import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppNav from '../components/AppNav';
import ChatSidebar from '../components/chat/ChatSidebar';
import ChatPanel from '../components/chat/ChatPanel';
import { apiClient } from '../api';
import { useAuth } from '../context/AuthContext';
import { useOnlineUsers } from '../hooks/useOnlineUsers';
import { TYPING_INDICATOR_TIMEOUT_MS, TYPING_THROTTLE_MS } from '../constants';
import { toast } from 'sonner';

const Chat = () => {
  const { userId: partnerId } = useParams();
  const navigate = useNavigate();
  const { user, wsConnected, wsSubscribe, wsSend } = useAuth();
  const { online } = useOnlineUsers();

  const [conversations, setConversations] = useState([]);
  const [partnerProfile, setPartnerProfile] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [pendingImage, setPendingImage] = useState(null);
  const [partnerTyping, setPartnerTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(0);

  const loadConversations = useCallback(async () => {
    try {
      const res = await apiClient.get('/messages/conversations');
      setConversations(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  useEffect(() => {
    if (!partnerId) { setMessages([]); setPartnerProfile(null); return; }
    (async () => {
      try {
        const [msgRes, profRes] = await Promise.all([
          apiClient.get(`/messages/${partnerId}`),
          apiClient.get(`/profiles/${partnerId}`),
        ]);
        setMessages(msgRes.data);
        setPartnerProfile(profRes.data);
      } catch (e) { console.error(e); }
    })();
  }, [partnerId]);

  // Subscribe to the shared WebSocket for chat events
  useEffect(() => {
    return wsSubscribe((data) => {
      if (data?.type === 'message') {
        const m = data.message;
        if (partnerId && (m.sender_id === partnerId || m.receiver_id === partnerId)) {
          setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
          if (m.sender_id === partnerId) setPartnerTyping(false);
        }
        loadConversations();
      } else if (data?.type === 'typing' && partnerId && data.from === partnerId) {
        setPartnerTyping(true);
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => setPartnerTyping(false), TYPING_INDICATOR_TIMEOUT_MS);
      } else if (data?.type === 'stop_typing' && partnerId && data.from === partnerId) {
        setPartnerTyping(false);
      }
    });
  }, [partnerId, wsSubscribe, loadConversations]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = (e) => {
    e.preventDefault();
    const content = input.trim();
    if ((!content && !pendingImage) || !partnerId) return;
    const payload = { type: 'message', receiver_id: partnerId, content, image_base64: pendingImage };
    const sent = wsSend(payload);
    if (sent) {
      wsSend({ type: 'stop_typing', receiver_id: partnerId });
      lastTypingSentRef.current = 0;
    } else {
      apiClient.post('/messages', {
        receiver_id: partnerId,
        content,
        image_base64: pendingImage,
      }).then((r) => {
        setMessages((prev) => [...prev, r.data]);
        loadConversations();
      }).catch((err) => {
        toast.error('Send failed', { description: err.response?.data?.detail || '' });
      });
    }
    setInput('');
    setPendingImage(null);
  };

  // Notify partner when typing (throttled to 1 ping / 2.5s)
  const handleInputChange = (val) => {
    setInput(val);
    if (!partnerId) return;
    const now = Date.now();
    if (now - lastTypingSentRef.current > TYPING_THROTTLE_MS) {
      if (wsSend({ type: 'typing', receiver_id: partnerId })) {
        lastTypingSentRef.current = now;
      }
    }
  };

  return (
    <div className="min-h-screen bg-fnl-dark">
      <AppNav />
      <div className="max-w-7xl mx-auto px-4 md:px-10 py-6">
        <div className="grid md:grid-cols-[320px_1fr] gap-4 h-[calc(100vh-130px)]">
          <ChatSidebar
            conversations={conversations}
            partnerId={partnerId}
            online={online}
            wsConnected={wsConnected}
            onPick={(id) => navigate(`/chat/${id}`)}
          />
          <ChatPanel
            partnerId={partnerId}
            partnerProfile={partnerProfile}
            messages={messages}
            currentUserId={user?.id}
            online={online}
            input={input}
            setInput={handleInputChange}
            pendingImage={pendingImage}
            setPendingImage={setPendingImage}
            onSend={send}
            endRef={messagesEndRef}
            partnerTyping={partnerTyping}
          />
        </div>
      </div>
    </div>
  );
};

export default Chat;
